import { useState } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

export interface InlineSelectOption {
  value: string;
  label: string;
  /** Hex color used on the dot / priority mark, e.g. '#e85d75'. */
  color: string;
  disabled?: boolean;
}

interface InlineSelectProps {
  value: string;
  options: InlineSelectOption[];
  onChange: (value: string) => void;
  /** Mark shown before the value: colored dot (status) or priority chevrons. */
  iconType?: 'dot' | 'priority';
  /** Uppercase label rendered to the left of the control. */
  label?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

// Design tokens from the handoff doc.
const TOKENS = {
  ink: '#2b3142',
  inkMute: '#8b94a3',
  line: '#e9ecef',
  lineSoft: '#f1f3f5',
  lineHover: '#bfc8d4',
  blue: '#3b82c4',
};

const Dot = ({ color, size = 8 }: { color: string; size?: number }): JSX.Element => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

const PriorityIcon = ({ value, color, size = 14 }: { value: string; color: string; size?: number }): JSX.Element => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: color,
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (value === 'HIGH') {
    return (
      <svg {...common}>
        <polyline points="3 10 8 5 13 10" />
        <polyline points="3 13 8 8 13 13" />
      </svg>
    );
  }
  if (value === 'MEDIUM') {
    return (
      <svg {...common}>
        <line x1="3" y1="6" x2="13" y2="6" />
        <line x1="3" y1="10" x2="13" y2="10" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <polyline points="3 6 8 11 13 6" />
      <polyline points="3 9 8 14 13 9" />
    </svg>
  );
};

const labelStyle: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  color: TOKENS.inkMute,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
};

const InlineSelect = ({
  value,
  options,
  onChange,
  iconType = 'dot',
  label,
  disabled = false,
  ariaLabel,
  className,
}: InlineSelectProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const current = options.find((option) => option.value === value) || options[0];

  const handleSelect = (option: InlineSelectOption) => {
    if (option.disabled || option.value === value) return;
    onChange(option.value);
  };

  const renderMark = (option: InlineSelectOption) =>
    iconType === 'priority' ? <PriorityIcon value={option.value} color={option.color} /> : <Dot color={option.color} />;

  const toggleStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    background: disabled ? '#fbfcfd' : '#fff',
    border: `1px solid ${open ? TOKENS.blue : TOKENS.line}`,
    borderRadius: 6,
    padding: '5px 8px 5px 10px',
    cursor: disabled ? 'default' : 'pointer',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'inherit',
    color: disabled ? TOKENS.inkMute : TOKENS.ink,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    transition: 'border-color .15s, box-shadow .15s',
    boxShadow: open ? '0 0 0 3px rgba(59,130,196,.15)' : 'none',
    opacity: disabled ? 0.75 : 1,
  };

  const control = (
    <Dropdown isOpen={open} toggle={() => !disabled && setOpen((prev) => !prev)} className={className}>
      <DropdownToggle
        tag="button"
        type="button"
        aria-label={ariaLabel}
        disabled={disabled}
        style={toggleStyle}
        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!open && !disabled) e.currentTarget.style.borderColor = TOKENS.lineHover;
        }}
        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!open && !disabled) e.currentTarget.style.borderColor = TOKENS.line;
        }}
      >
        {current && renderMark(current)}
        <span>{current?.label}</span>
        {!disabled && (
          <span style={{ color: TOKENS.inkMute, marginLeft: 2, display: 'inline-flex' }}>
            <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10 }} aria-hidden="true" />
          </span>
        )}
      </DropdownToggle>
      <DropdownMenu
        right
        className="border p-1"
        style={{
          minWidth: 180,
          borderRadius: 8,
          borderColor: TOKENS.line,
          boxShadow: '0 8px 24px rgba(20,30,50,.12), 0 2px 6px rgba(20,30,50,.06)',
        }}
      >
        {options.map((option) => {
          const isActive = option.value === value;
          return (
            <DropdownItem
              key={option.value}
              disabled={option.disabled}
              onClick={() => handleSelect(option)}
              className="d-flex align-items-center px-2 py-2"
              style={{
                fontWeight: 600,
                fontSize: 13,
                color: TOKENS.ink,
                borderRadius: 5,
                background: isActive ? TOKENS.lineSoft : 'transparent',
              }}
            >
              {renderMark(option)}
              <span className="ml-2">{option.label}</span>
              {isActive && (
                <i className="fas fa-check ml-auto" style={{ fontSize: 11, color: TOKENS.blue }} aria-hidden="true" />
              )}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );

  if (!label) return control;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={labelStyle}>{label}</span>
      {control}
    </span>
  );
};

export default InlineSelect;
